import type { DateRange } from '../hooks/useDateRangeFilter';

/**
 * Parsea una fecha en formato DD/MM/YYYY (formato devuelto por el backend).
 * Devuelve null si la cadena no tiene el formato esperado.
 */
export function parseFecha(ddmmyyyy: string): Date | null {
  const parts = ddmmyyyy.split('/');
  if (parts.length !== 3) return null;
  const [d, m, y] = parts;
  if (!d || !m || !y) return null;
  const day = Number(d);
  const month = Number(m);
  const year = Number(y);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  return new Date(year, month - 1, day);
}

/**
 * Comprueba si una fecha en formato DD/MM/YYYY está dentro del rango dado.
 * Si range es null, siempre devuelve true (sin filtro activo).
 */
export function matchesDateRange(fecha: string, range: DateRange | null): boolean {
  if (!range) return true;
  const fechaDate = parseFecha(fecha);
  if (!fechaDate) return true; // fecha inválida: no filtrar

  // Normalizar a medianoche para comparar solo por día
  const start = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate());
  const end   = new Date(range.end.getFullYear(),   range.end.getMonth(),   range.end.getDate());
  const date  = new Date(fechaDate.getFullYear(),    fechaDate.getMonth(),   fechaDate.getDate());

  return date >= start && date <= end;
}

/**
 * Formatea una Date a YYYY-MM-DD (formato que espera el backend en querystring).
 */
function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface DateRangeApiParams {
  fecha_inicio?: string;
  fecha_fin?: string;
}

/**
 * Convierte un DateRange del filtro a los query params que espera la API.
 * Devuelve un objeto vacío si no hay rango activo.
 */
export function toApiDateParams(range: DateRange | null): DateRangeApiParams {
  if (!range) return {};
  return {
    fecha_inicio: toIsoDate(range.start),
    fecha_fin: toIsoDate(range.end),
  };
}

/**
 * Determina si corresponde mostrar la card "Utilidad a Liquidar".
 * Solo tiene sentido del mes actual en adelante: si el período seleccionado
 * termina antes del inicio del mes en curso (un mes pasado), la utilidad de
 * ese período ya está total o parcialmente liquidada y no se muestra "a liquidar".
 * Sin rango activo (fallback), se muestra igual.
 */
export function shouldShowUtilidadALiquidar(range: DateRange | null): boolean {
  if (!range) return true;
  const now = new Date();
  const inicioMesActual = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(range.end.getFullYear(), range.end.getMonth(), range.end.getDate());
  return end >= inicioMesActual;
}
